//Node Modules
import React from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

//Pages

//BinaryForge Components
import { AppHeaderGuest } from "../../components/layout";

//3rd Party Components
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { classNames } from "primereact/utils";

//Atoms
import { loggedInUserAtom } from "../../atoms/user";
import { toastAtom } from "../../atoms/toast";

//Helpers
import { useGetUserSchemeConfig } from "../../helpers/User";

//Other
import { nav } from "../../config/navigation";
import { passwordValidation } from "../../config/user";

const CompleteRegistration = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { handleGetAdminConfig, handleGetUserConfig } = useGetUserSchemeConfig();

	const loginUser = useRecoilValue(loggedInUserAtom);
	const [toast, setToast] = useRecoilState(toastAtom);
	const setLoggedInUser = useSetRecoilState(loggedInUserAtom);

	//React Hook Form
	const defaultValues = {
		username: loginUser ? loginUser.username : "",
		passwordCurrent: loginUser ? loginUser.password : "",
		passwordNew: "",
		passwordConfirm: "",
	};
	const {
		control,
		getValues,
		formState: { errors, isSubmitting },
		handleSubmit,
	} = useForm({
		defaultValues,
		mode: "onTouched",
		reValidateMode: "onChange",
	});

	// Form Error Message
	const getFormErrorMessage = (name) => {
		return errors[name] && <span className="fontColour-error fontSize-small">{errors[name].message}</span>;
	};

	// Login Form Submit
	const onChangePassword = async (data) => {
		let toPage = nav.guest.logout;
		try {
			const userWithPendingStatus = await Auth.signIn(data.username, data.passwordCurrent);

			if (userWithPendingStatus.challengeParam) {
				await Auth.completeNewPassword(userWithPendingStatus, data.passwordNew);
				setToast({
					...toast,
					severity: "success",
					summary: t("login.toast.completeRegistration.summary"),
					detail: t("login.toast.completeRegistration.detail"),
				});

				const userPayload = userWithPendingStatus.signInUserSession.idToken.payload;

				if (userPayload["cognito:groups"]) {
					const isAdmin = userPayload["cognito:groups"].includes("administrator");
					toPage = isAdmin
						? await handleGetAdminConfig(null)
						: await handleGetUserConfig(userPayload["sub"], null);

					setLoggedInUser({
						sub: userPayload["sub"],
						username: userPayload["cognito:username"],
						fullname: userPayload["name"],
						role: isAdmin ? "ADMINISTRATOR" : null,
					});

					navigate(toPage);
				} else {
					throw Error(t("login.error.noGroup"));
				}
			} else {
				navigate(nav.guest.login);
				throw Error(t("login.error.alreadyRegistered", { username: data.username }));
			}
		} catch (err) {
			console.error("onSubmit Error:", err);
			setToast({
				...toast,
				severity: "error",
				summary: t("login.toast.loginFail.summary"),
				detail: t("login.toast.loginFail.detail", { error: err.message }),
			});
		}
	};

	return (
		<main className="appMain">
			<div className="appMain-content">
				<AppHeaderGuest />

				<form onSubmit={handleSubmit(onChangePassword)}>
					<div className="formFieldsWrapper">
						<div className="formField">
							<label htmlFor="username">{t("login.username.label")}</label>
							<Controller
								name="username"
								control={control}
								rules={{ required: t("common.form.required") }}
								render={({ field, fieldState }) => (
									<InputText
										{...field}
										id={field.name}
										className={classNames({ "p-error": fieldState.error })}
									/>
								)}
							/>
							{getFormErrorMessage("username")}
						</div>
						<div className="formField">
							<label htmlFor="passwordCurrent">{t("login.passwordCurrent.label")}</label>
							<Controller
								name="passwordCurrent"
								control={control}
								rules={{ required: t("common.form.required") }}
								render={({ field: { ref, ...newField }, fieldState }) => (
									<Password
										{...newField}
										inputRef={ref}
										feedback={false}
										toggleMask={true}
										className={classNames({ "p-error": fieldState.error })}
									/>
								)}
							/>
							{getFormErrorMessage("passwordCurrent")}
						</div>
						<div className="formField">
							<label htmlFor="passwordNew">{t("login.passwordNew.label")}</label>
							<Controller
								name="passwordNew"
								control={control}
								rules={{
									required: t("common.form.required"),
									pattern: {
										value: passwordValidation,
										message: t("common.form.password"),
									},
									validate: {
										match: (value) =>
											getValues("passwordCurrent") !== value || t("login.passwordNew.match"),
									},
								}}
								render={({ field: { ref, ...newField }, fieldState }) => (
									<Password
										{...newField}
										inputRef={ref}
										feedback={false}
										toggleMask={true}
										className={classNames({ "p-error": fieldState.error })}
									/>
								)}
							/>
							{getFormErrorMessage("passwordNew")}
						</div>
						<div className="formField">
							<label htmlFor="passwordConfirm">{t("login.passwordConfirm.label")}</label>
							<Controller
								name="passwordConfirm"
								control={control}
								rules={{
									required: t("common.form.required"),
									validate: {
										match: (value) =>
											getValues("passwordNew") === value || t("login.passwordConfirm.match"),
									},
								}}
								render={({ field: { ref, ...newField }, fieldState }) => (
									<Password
										{...newField}
										inputRef={ref}
										feedback={false}
										toggleMask={true}
										className={classNames({ "p-error": fieldState.error })}
									/>
								)}
							/>
							{getFormErrorMessage("passwordConfirm")}
						</div>
					</div>

					<Button
						label={isSubmitting ? t("login.completingRegistration") : t("login.completeRegistration")}
						type="submit"
						className="feature"
						disabled={isSubmitting}
					/>
				</form>
			</div>
		</main>
	);
};

export default CompleteRegistration;
